import { useEffect, useMemo, useRef, useState } from 'react';
import axios from '@/lib/axios';
import { toast } from 'sonner';
import {
  LEGACY_DOC_UNSUPPORTED_MESSAGE,
  UPLOAD_IN_PROGRESS_MESSAGE,
  beginUploadBatch,
  finishUploadBatch,
  isLegacyDocFile,
  uploadDraftingDnaSamplesSequentially,
} from './advocateStyleUploadLogic';
import { isPendingStyleSample, resolveCompileTargets } from './advocateStyleCompileLogic';
import {
  DRAFTING_DNA_REVIEW_SECTIONS,
  getDefaultReviewSection,
  getSelectedReviewSection,
  normalizeDraftingDnaReviewResult,
} from './advocateStyleReviewLogic';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Loader2, Sparkles, Star, Trash2, Upload, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const statusTone = {
  pending: 'bg-amber-100 text-amber-900 border-amber-200',
  uploaded: 'bg-sky-100 text-sky-900 border-sky-200',
  analyzed: 'bg-emerald-100 text-emerald-900 border-emerald-200',
  error: 'bg-rose-100 text-rose-900 border-rose-200',
};

const stepMeta = [
  {
    number: '01',
    title: 'Step 1 — Upload Sample Opinions',
    description: 'Upload one or more proven legal opinions that represent the drafting style you want to preserve.',
  },
  {
    number: '02',
    title: 'Step 2 — Compile Drafting DNA',
    description: 'Compile section-specific drafting patterns such as terminology, sequencing, tone, and recommendation style.',
  },
  {
    number: '03',
    title: 'Step 3 — Review Result',
    description: 'Review the learned drafting standard section by section, with representative masked excerpts where evidence is strong enough.',
  },
];

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const StepCard = ({ step, isActive, children, testId }) => (
  <Card
    className={`border transition-all duration-200 ${isActive ? 'border-orange-300 bg-orange-50/80 shadow-sm' : 'border-slate-200 bg-white'}`}
    data-testid={testId}
  >
    <CardHeader className="space-y-3 pb-4">
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-2">
          <Badge className={`rounded-full px-3 py-1 text-xs ${isActive ? 'bg-orange-700 text-white' : 'bg-slate-200 text-slate-700'}`} data-testid={`${testId}-badge`}>
            {step.number}
          </Badge>
          <CardTitle className="text-xl text-slate-900" data-testid={`${testId}-title`}>{step.title}</CardTitle>
          <CardDescription className="max-w-2xl text-sm text-slate-600" data-testid={`${testId}-description`}>
            {step.description}
          </CardDescription>
        </div>
      </div>
    </CardHeader>
    <CardContent className="pt-0">{children}</CardContent>
  </Card>
);

const SampleRow = ({ sample, onDelete }) => (
  <div
    className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3"
    data-testid={`drafting-dna-sample-row-${sample.id}`}
  >
    <div className="min-w-0 flex-1 space-y-1">
      <p className="truncate text-sm font-medium text-slate-900" data-testid={`drafting-dna-sample-name-${sample.id}`}>
        {sample.filename}
      </p>
      <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
        <Badge className={`border ${statusTone[sample.status] || 'bg-slate-100 text-slate-800 border-slate-200'}`} data-testid={`drafting-dna-sample-status-${sample.id}`}>
          {sample.status}
        </Badge>
        <span data-testid={`drafting-dna-sample-filetype-${sample.id}`}>{sample.file_type?.toUpperCase()}</span>
      </div>
    </div>
    <Button
      type="button"
      variant="ghost"
      className="rounded-full text-rose-700 hover:bg-rose-50 hover:text-rose-800"
      onClick={() => onDelete(sample.id)}
      data-testid={`drafting-dna-sample-delete-${sample.id}`}
    >
      <Trash2 className="mr-2 h-4 w-4" /> Remove
    </Button>
  </div>
);

const ProfileRow = ({ profile, isSelected, onSelect, onSetDefault, onDelete }) => (
  <div
    className={`rounded-2xl border px-4 py-3 transition-all ${isSelected ? 'border-teal-400 bg-teal-50' : 'border-slate-200 bg-white'}`}
    data-testid={`drafting-dna-profile-row-${profile.id}`}
  >
    <div className="flex flex-wrap items-start justify-between gap-3">
      <button
        type="button"
        className="min-w-0 flex-1 text-left"
        onClick={() => onSelect(profile.id)}
        data-testid={`drafting-dna-profile-select-${profile.id}`}
      >
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-sm font-semibold text-slate-900">{profile.label}</p>
          {profile.is_default && (
            <Badge className="bg-teal-700 text-white" data-testid={`drafting-dna-profile-default-${profile.id}`}>
              <Star className="mr-1 h-3 w-3" /> Default
            </Badge>
          )}
        </div>
        <p className="mt-1 text-xs text-slate-500" data-testid={`drafting-dna-profile-meta-${profile.id}`}>
          {profile.sample_count || 0} opinion(s) · confidence {Math.round((profile.confidence_score || 0) * 100)}%
        </p>
      </button>
      <div className="flex flex-wrap items-center gap-2">
        {!profile.is_default && (
          <Button
            type="button"
            variant="outline"
            className="rounded-full"
            onClick={() => onSetDefault(profile.id)}
            data-testid={`drafting-dna-profile-default-button-${profile.id}`}
          >
            Set Default
          </Button>
        )}
        <Button
          type="button"
          variant="ghost"
          className="rounded-full text-rose-700 hover:bg-rose-50 hover:text-rose-800"
          onClick={() => onDelete(profile.id)}
          data-testid={`drafting-dna-profile-delete-${profile.id}`}
        >
          <Trash2 className="mr-2 h-4 w-4" /> Delete
        </Button>
      </div>
    </div>
  </div>
);

export default function AdvocateStyleModal({ trigger, onStyleChange }) {
  const [open, setOpen] = useState(false);
  const [profiles, setProfiles] = useState([]);
  const [samples, setSamples] = useState([]);
  const [dnaLabel, setDnaLabel] = useState('Drafting DNA');
  const [currentProfileId, setCurrentProfileId] = useState('');
  const [selectedSection, setSelectedSection] = useState(getDefaultReviewSection());
  const [reviewResult, setReviewResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [compiling, setCompiling] = useState(false);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [compileProgress, setCompileProgress] = useState({ percent: 0, message: '' });
  const [currentUploadBatchId, setCurrentUploadBatchId] = useState(null);
  const [currentUploadSampleIds, setCurrentUploadSampleIds] = useState([]);
  const uploadLockRef = useRef(false);

  const pendingSamples = useMemo(
    () => samples.filter((sample) => isPendingStyleSample(sample)),
    [samples],
  );
  const compileTargets = useMemo(
    () => resolveCompileTargets({ samples, currentUploadBatchId, currentUploadSampleIds }),
    [samples, currentUploadBatchId, currentUploadSampleIds],
  );
  const activeReviewSection = useMemo(
    () => getSelectedReviewSection(reviewResult, selectedSection),
    [reviewResult, selectedSection],
  );
  const activeStep = reviewResult ? 3 : (pendingSamples.length > 0 || compiling ? 2 : 1);

  const fetchProfiles = async () => {
    const response = await axios.get(`${API}/advocate-style/profiles`);
    return response.data || [];
  };

  const fetchSamples = async () => {
    const response = await axios.get(`${API}/advocate-style/samples`);
    return response.data || [];
  };

  const fetchReviewResult = async (profileId) => {
    if (!profileId) {
      setReviewResult(null);
      return null;
    }

    setReviewLoading(true);
    try {
      const response = await axios.get(`${API}/advocate-style/profiles/${profileId}/review-result`);
      const normalized = normalizeDraftingDnaReviewResult(response.data);
      setReviewResult(normalized);
      setSelectedSection((current) => normalized.sections[current] ? current : getDefaultReviewSection());
      return normalized;
    } catch (error) {
      console.error('Failed to load review result', error);
      setReviewResult(null);
      toast.error(error?.response?.data?.detail || 'Unable to load Review Result.');
      return null;
    } finally {
      setReviewLoading(false);
    }
  };

  const loadStudioData = async ({ nextProfileId } = {}) => {
    setLoading(true);
    try {
      const [profilesData, samplesData] = await Promise.all([fetchProfiles(), fetchSamples()]);
      setProfiles(profilesData);
      setSamples(samplesData);

      const preferredProfileId = nextProfileId
        || currentProfileId
        || profilesData.find((profile) => profile.is_default)?.id
        || profilesData[0]?.id
        || '';

      setCurrentProfileId(preferredProfileId);
      if (preferredProfileId) {
        await fetchReviewResult(preferredProfileId);
      } else {
        setReviewResult(null);
      }
    } catch (error) {
      console.error('Failed to load Drafting DNA Studio', error);
      toast.error(error?.response?.data?.detail || 'Unable to load Drafting DNA Studio.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      loadStudioData();
    }
  }, [open]);

  const handleOpenChange = (nextOpen) => {
    setOpen(nextOpen);
    if (!nextOpen) {
      setCompileProgress({ percent: 0, message: '' });
    }
  };

  const handleFilesSelected = async (event) => {
    const incomingFiles = Array.from(event.target.files || []);
    event.target.value = '';
    if (incomingFiles.length === 0) {
      return;
    }

    const legacyDoc = incomingFiles.find((file) => isLegacyDocFile(file.name));
    if (legacyDoc) {
      toast.error(LEGACY_DOC_UNSUPPORTED_MESSAGE);
      return;
    }

    if (!beginUploadBatch(uploadLockRef)) {
      toast.error(UPLOAD_IN_PROGRESS_MESSAGE);
      return;
    }

    const uploadBatchId = `drafting-dna-${Date.now()}`;
    setUploading(true);
    try {
      const results = await uploadDraftingDnaSamplesSequentially({
        files: incomingFiles,
        uploadFile: async (file) => {
          const formData = new FormData();
          formData.append('file', file);
          formData.append('upload_batch_id', uploadBatchId);
          await axios.post(`${API}/advocate-style/samples/upload`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
        },
      });

      const successCount = results.filter((result) => result.success).length;
      const failedCount = results.length - successCount;

      if (successCount > 0) {
        setCurrentUploadBatchId(uploadBatchId);
        await loadStudioData();
        const refreshedSamples = await fetchSamples();
        const uploadedIds = refreshedSamples
          .filter((sample) => sample?.metadata?.upload_batch_id === uploadBatchId)
          .map((sample) => sample.id)
          .filter(Boolean);
        setSamples(refreshedSamples);
        setCurrentUploadSampleIds(uploadedIds);
        setDnaLabel((current) => (current && current !== 'Drafting DNA' ? current : `Drafting DNA ${new Date().toLocaleDateString()}`));
        toast.success(`${successCount} opinion(s) uploaded successfully.`);
      }

      if (failedCount > 0) {
        toast.error(`${failedCount} opinion(s) could not be uploaded.`);
      }
    } catch (error) {
      console.error('Upload failed', error);
      toast.error(error?.response?.data?.detail || 'Upload failed.');
    } finally {
      setUploading(false);
      finishUploadBatch(uploadLockRef);
    }
  };

  const pollCompileJob = async (jobId) => {
    for (let attempt = 0; attempt < 90; attempt += 1) {
      const response = await axios.get(`${API}/advocate-style/analyze/status/${jobId}`);
      const payload = response.data || {};
      if (payload.progress) {
        setCompileProgress({
          percent: payload.progress.percent || 0,
          message: payload.current_step || payload.message || 'Compiling Drafting DNA...',
        });
      }

      if (payload.status === 'completed') {
        return payload;
      }
      if (payload.status === 'failed') {
        throw new Error(payload.error || payload.message || 'Drafting DNA compilation failed.');
      }

      await wait(2000);
    }

    throw new Error('Drafting DNA compilation timed out.');
  };

  const handleCompile = async () => {
    if (!compileTargets.hasTargets) {
      toast.error('Upload at least one accepted opinion before compiling Drafting DNA.');
      return;
    }

    setCompiling(true);
    setCompileProgress({ percent: 8, message: 'Preparing Drafting DNA compilation...' });
    try {
      const payload = {
        label: (dnaLabel || 'Drafting DNA').trim(),
        sample_ids: compileTargets.sampleIds,
        upload_batch_id: compileTargets.uploadBatchId,
      };

      const response = await axios.post(`${API}/advocate-style/analyze`, payload);
      const result = await pollCompileJob(response.data.job_id);
      const profileId = result.profile?.id || result.result?.profile?.id;

      setCurrentUploadSampleIds([]);
      setCurrentUploadBatchId(null);
      setCompileProgress({ percent: 100, message: 'Drafting DNA compilation complete.' });

      await loadStudioData({ nextProfileId: profileId || undefined });
      if (profileId) {
        setCurrentProfileId(profileId);
        await fetchReviewResult(profileId);
      }

      toast.success('Drafting DNA compiled successfully.');
      if (onStyleChange) {
        onStyleChange(result.profile || result.result?.profile || null);
      }
    } catch (error) {
      console.error('Compilation failed', error);
      toast.error(error?.message || error?.response?.data?.detail || 'Drafting DNA compilation failed.');
    } finally {
      setCompiling(false);
    }
  };

  const handleDeleteSample = async (sampleId) => {
    try {
      await axios.delete(`${API}/advocate-style/samples/${sampleId}`);
      setCurrentUploadSampleIds((current) => current.filter((id) => id !== sampleId));
      await loadStudioData();
      toast.success('Sample opinion removed.');
    } catch (error) {
      console.error('Failed to delete sample', error);
      toast.error(error?.response?.data?.detail || 'Unable to remove sample opinion.');
    }
  };

  const handleDeleteProfile = async (profileId) => {
    try {
      await axios.delete(`${API}/advocate-style/profiles/${profileId}`);
      const nextProfiles = profiles.filter((profile) => profile.id !== profileId);
      const nextProfileId = currentProfileId === profileId
        ? (nextProfiles.find((profile) => profile.is_default)?.id || nextProfiles[0]?.id || '')
        : currentProfileId;
      await loadStudioData({ nextProfileId });
      toast.success('Drafting DNA deleted.');
      if (onStyleChange) {
        onStyleChange(null);
      }
    } catch (error) {
      console.error('Failed to delete Drafting DNA', error);
      toast.error(error?.response?.data?.detail || 'Unable to delete Drafting DNA.');
    }
  };

  const handleSetDefault = async (profileId) => {
    try {
      await axios.post(`${API}/advocate-style/set-default`, { style_id: profileId });
      await loadStudioData({ nextProfileId: profileId });
      toast.success('Default Drafting DNA updated.');
      if (onStyleChange) {
        const selectedProfile = profiles.find((profile) => profile.id === profileId) || null;
        onStyleChange(selectedProfile);
      }
    } catch (error) {
      console.error('Failed to set default Drafting DNA', error);
      toast.error(error?.response?.data?.detail || 'Unable to update the default Drafting DNA.');
    }
  };

  const acceptedOpinionCount = samples.length;
  const selectedReviewStatus = reviewResult?.status || 'requires_recompilation';
  const reviewStatusLabel = selectedReviewStatus === 'compiled'
    ? 'Compiled'
    : selectedReviewStatus === 'requires_more_samples'
      ? 'Requires More Samples'
      : 'Requires Recompilation';

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="w-[95vw] max-w-7xl overflow-hidden border-0 bg-gradient-to-br from-[#fffaf3] via-[#fffdf8] to-[#f5fbfb] p-0" data-testid="drafting-dna-studio-modal">
        <div className="flex max-h-[90vh] flex-col">
          <DialogHeader className="border-b border-slate-200 bg-[radial-gradient(circle_at_top_left,_rgba(249,115,22,0.16),_transparent_35%),linear-gradient(120deg,_rgba(15,118,110,0.08),_rgba(255,255,255,0.9))] px-6 py-6 text-left sm:px-8">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="space-y-3">
                <Badge className="rounded-full bg-slate-900 px-3 py-1 text-white" data-testid="drafting-dna-studio-badge">
                  <Sparkles className="mr-2 h-3.5 w-3.5" /> Drafting DNA Studio
                </Badge>
                <DialogTitle className="text-4xl text-slate-950" data-testid="drafting-dna-studio-title">
                  Compile your drafting style into a reusable standard.
                </DialogTitle>
                <DialogDescription className="max-w-3xl text-sm text-slate-600" data-testid="drafting-dna-studio-description">
                  Upload sample opinions, compile Drafting DNA, and review the compiled section-wise result without using a runtime comparison.
                </DialogDescription>
              </div>
              <Button type="button" variant="ghost" className="rounded-full" onClick={() => setOpen(false)} data-testid="drafting-dna-studio-close-button">
                <X className="mr-2 h-4 w-4" /> Close
              </Button>
            </div>
          </DialogHeader>

          <ScrollArea className="flex-1 px-6 py-6 sm:px-8">
            <div className="space-y-6 pb-6">
              <div className="grid gap-6 xl:grid-cols-[1.15fr_0.85fr]">
                <StepCard step={stepMeta[0]} isActive={activeStep === 1} testId="drafting-dna-step-1-card">
                  <div className="space-y-5">
                    <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-dashed border-orange-300 bg-white/80 px-4 py-4">
                      <div>
                        <p className="text-sm font-semibold text-slate-900" data-testid="drafting-dna-upload-count-label">Total accepted opinions</p>
                        <p className="text-2xl font-semibold text-slate-950" data-testid="drafting-dna-upload-count-value">{acceptedOpinionCount}</p>
                      </div>
                      <label
                        htmlFor="drafting-dna-upload-input"
                        className="inline-flex cursor-pointer items-center rounded-full bg-slate-900 px-4 py-2 text-sm font-medium text-white transition hover:bg-slate-800"
                        data-testid="drafting-dna-upload-trigger"
                      >
                        {uploading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
                        Upload Sample Opinions
                      </label>
                      <input
                        id="drafting-dna-upload-input"
                        type="file"
                        multiple
                        accept=".pdf,.docx"
                        className="hidden"
                        onChange={handleFilesSelected}
                        data-testid="drafting-dna-upload-input"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="drafting-dna-name-input" data-testid="drafting-dna-name-label">Drafting DNA Name</Label>
                      <Input
                        id="drafting-dna-name-input"
                        value={dnaLabel}
                        onChange={(event) => setDnaLabel(event.target.value)}
                        placeholder="e.g. Senior Conveyancing Style"
                        data-testid="drafting-dna-name-input"
                      />
                    </div>

                    <div className="space-y-3" data-testid="drafting-dna-sample-list">
                      {loading ? (
                        <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-5 text-sm text-slate-600" data-testid="drafting-dna-sample-loading">
                          <Loader2 className="h-4 w-4 animate-spin" /> Loading uploaded opinions...
                        </div>
                      ) : samples.length === 0 ? (
                        <div className="rounded-2xl border border-slate-200 bg-white px-4 py-6 text-sm text-slate-600" data-testid="drafting-dna-sample-empty-state">
                          Upload one or more sample opinions to begin compiling Drafting DNA.
                        </div>
                      ) : (
                        samples.map((sample) => (
                          <SampleRow key={sample.id} sample={sample} onDelete={handleDeleteSample} />
                        ))
                      )}
                    </div>
                  </div>
                </StepCard>

                <StepCard step={stepMeta[1]} isActive={activeStep === 2} testId="drafting-dna-step-2-card">
                  <div className="space-y-5">
                    <div className="rounded-2xl border border-slate-200 bg-white p-4">
                      <p className="text-sm font-semibold text-slate-900" data-testid="drafting-dna-compile-targets-label">Opinions ready for compilation</p>
                      <p className="mt-2 text-3xl font-semibold text-slate-950" data-testid="drafting-dna-compile-targets-value">
                        {compileTargets.sampleIds.length}
                      </p>
                      <p className="mt-2 text-sm text-slate-600" data-testid="drafting-dna-compile-source">
                        {compileTargets.hasTargets
                          ? 'The compiler will preserve section-specific drafting patterns from the accepted opinions shown in Step 1.'
                          : 'Upload sample opinions in Step 1 to enable compilation.'}
                      </p>
                    </div>

                    {(compiling || compileProgress.percent > 0) && (
                      <div className="space-y-3 rounded-2xl border border-teal-200 bg-teal-50 p-4" data-testid="drafting-dna-compile-progress-panel">
                        <div className="flex items-center justify-between gap-3">
                          <p className="text-sm font-semibold text-teal-900" data-testid="drafting-dna-compile-progress-message">
                            {compileProgress.message || 'Compiling Drafting DNA...'}
                          </p>
                          <span className="text-sm font-medium text-teal-900" data-testid="drafting-dna-compile-progress-percent">
                            {compileProgress.percent}%
                          </span>
                        </div>
                        <Progress value={compileProgress.percent} data-testid="drafting-dna-compile-progress-bar" />
                      </div>
                    )}

                    <Button
                      type="button"
                      className="h-12 w-full rounded-full bg-orange-600 text-white hover:bg-orange-700"
                      onClick={handleCompile}
                      disabled={compiling || !compileTargets.hasTargets}
                      data-testid="drafting-dna-compile-button"
                    >
                      {compiling ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />}
                      Compile Drafting DNA
                    </Button>
                  </div>
                </StepCard>
              </div>

              <StepCard step={stepMeta[2]} isActive={activeStep === 3} testId="drafting-dna-step-3-card">
                <div className="grid gap-6 xl:grid-cols-[0.85fr_1.15fr]">
                  <div className="space-y-4">
                    <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="drafting-dna-review-summary-card">
                      <p className="text-sm font-semibold text-slate-900">Compiled Drafting DNA</p>
                      <p className="mt-2 text-sm text-slate-600" data-testid="drafting-dna-review-summary-text">
                        Review the compiled section output for any saved Drafting DNA profile.
                      </p>
                      <Badge className={`mt-3 ${selectedReviewStatus === 'compiled' ? 'bg-emerald-700 text-white' : 'bg-amber-600 text-white'}`} data-testid="drafting-dna-review-status-badge">
                        {reviewStatusLabel}
                      </Badge>
                    </div>

                    <div className="space-y-3" data-testid="drafting-dna-profile-list">
                      {profiles.length === 0 ? (
                        <div className="rounded-2xl border border-slate-200 bg-white px-4 py-6 text-sm text-slate-600" data-testid="drafting-dna-profile-empty-state">
                          Compile Drafting DNA to review the section-wise result here.
                        </div>
                      ) : (
                        profiles.map((profile) => (
                          <ProfileRow
                            key={profile.id}
                            profile={profile}
                            isSelected={profile.id === currentProfileId}
                            onSelect={async (profileId) => {
                              setCurrentProfileId(profileId);
                              await fetchReviewResult(profileId);
                            }}
                            onSetDefault={handleSetDefault}
                            onDelete={handleDeleteProfile}
                          />
                        ))
                      )}
                    </div>
                  </div>

                  <div className="space-y-4 rounded-[28px] border border-slate-200 bg-white p-5 shadow-sm" data-testid="drafting-dna-review-panel">
                    <div className="flex flex-wrap items-center justify-between gap-4">
                      <div className="space-y-1">
                        <Label htmlFor="drafting-dna-section-select" data-testid="drafting-dna-section-label">Section</Label>
                        <p className="text-xs text-slate-500" data-testid="drafting-dna-profile-name">
                          {reviewResult?.profile_name || 'No Drafting DNA selected'}
                        </p>
                      </div>
                      <div className="w-full max-w-sm">
                        <Select value={selectedSection} onValueChange={setSelectedSection} disabled={!reviewResult}>
                          <SelectTrigger id="drafting-dna-section-select" data-testid="drafting-dna-section-select">
                            <SelectValue placeholder="Choose a section" />
                          </SelectTrigger>
                          <SelectContent>
                            {DRAFTING_DNA_REVIEW_SECTIONS.map((section) => (
                              <SelectItem key={section.value} value={section.value} data-testid={`drafting-dna-section-option-${section.value}`}>
                                {section.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <Separator />

                    <div className="space-y-3" data-testid="drafting-dna-review-result-block">
                      <div>
                        <p className="text-sm font-semibold text-slate-500">Review Result</p>
                        <h3 className="mt-1 text-2xl font-semibold text-slate-950" data-testid="drafting-dna-review-section-title">
                          {activeReviewSection?.title || 'Review Result'}
                        </h3>
                      </div>

                      {reviewLoading ? (
                        <div className="flex min-h-[260px] items-center justify-center rounded-3xl border border-slate-200 bg-slate-50 text-sm text-slate-600" data-testid="drafting-dna-review-loading">
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading Review Result...
                        </div>
                      ) : activeReviewSection?.result ? (
                        <div className="min-h-[320px] rounded-3xl border border-slate-200 bg-[linear-gradient(180deg,_rgba(255,250,243,1)_0%,_rgba(255,255,255,1)_100%)] p-5" data-testid="drafting-dna-review-result-content">
                          <ScrollArea className="h-[320px] pr-4">
                            <div className="whitespace-pre-wrap text-sm leading-7 text-slate-800" data-testid="drafting-dna-review-result-text">
                              {activeReviewSection.result}
                            </div>
                          </ScrollArea>
                        </div>
                      ) : (
                        <div className="flex min-h-[260px] items-center justify-center rounded-3xl border border-slate-200 bg-slate-50 px-6 text-center text-sm text-slate-600" data-testid="drafting-dna-review-empty-state">
                          Compile Drafting DNA and select a section to see the compiled result.
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </StepCard>
            </div>
          </ScrollArea>
        </div>
      </DialogContent>
    </Dialog>
  );
}